import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	ArrowLeft,
	DollarSign,
	Star,
	Briefcase,
	Users,
	Clock,
} from "lucide-react";
import { CountdownTimer } from "./CountdownTimer";
import { BidComparison } from "./BidComparison";
import { BidForm } from "./BidForm";
import { CommentThread } from "./CommentThread";
import { fetchAuction, fetchUsers, submitComment } from "@/lib/api";
import type { Auction, User } from "@/lib/types";

const categoryColors: Record<string, string> = {
	"Web Development": "bg-blue-100 text-blue-700",
	Design: "bg-purple-100 text-purple-700",
	DevOps: "bg-orange-100 text-orange-700",
	Backend: "bg-emerald-100 text-emerald-700",
	Content: "bg-pink-100 text-pink-700",
	Mobile: "bg-cyan-100 text-cyan-700",
};

interface AuctionDetailProps {
	auctionId: string;
	onBack: () => void;
}

export function AuctionDetail({ auctionId, onBack }: AuctionDetailProps) {
	const [auction, setAuction] = useState<Auction | null>(null);
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(true);

	const loadData = async () => {
		const [a, u] = await Promise.all([fetchAuction(auctionId), fetchUsers()]);
		setAuction(a);
		setUsers(u);
		setLoading(false);
	};

	useEffect(() => {
		loadData();
		const interval = setInterval(async () => {
			const a = await fetchAuction(auctionId);
			setAuction(a);
		}, 5000);
		return () => clearInterval(interval);
	}, [auctionId]);

	if (loading || !auction) {
		return (
			<div className="flex items-center justify-center py-20">
				<div className="animate-pulse text-muted-foreground">Loading auction...</div>
			</div>
		);
	}

	const isClosed = auction.status === "closed";

	return (
		<div className="space-y-6">
			<Button variant="ghost" size="sm" onClick={onBack} className="gap-2 -ml-2">
				<ArrowLeft className="h-4 w-4" />
				Back to listings
			</Button>

			{/* Header */}
			<div>
				<div className="flex items-center gap-2 mb-2 flex-wrap">
					<Badge
						className={
							categoryColors[auction.category] ||
							"bg-muted text-muted-foreground"
						}
					>
						{auction.category}
					</Badge>
					{isClosed ? (
						<Badge variant="secondary">Closed</Badge>
					) : (
						<Badge variant="outline" className="gap-1">
							<span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
							Active
						</Badge>
					)}
				</div>
				<h1 className="text-2xl font-bold mb-2">{auction.title}</h1>
				<p className="text-muted-foreground leading-relaxed">
					{auction.description}
				</p>
			</div>

			{/* Stats Bar */}
			<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
				<Card>
					<CardContent className="p-3 flex items-center gap-3">
						<div className="p-2 rounded-lg bg-primary/10">
							<DollarSign className="h-4 w-4 text-primary" />
						</div>
						<div>
							<p className="text-xs text-muted-foreground">Budget</p>
							<p className="font-semibold text-sm">
								${auction.budget.toLocaleString()}
							</p>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-3 flex items-center gap-3">
						<div className="p-2 rounded-lg bg-primary/10">
							<Users className="h-4 w-4 text-primary" />
						</div>
						<div>
							<p className="text-xs text-muted-foreground">Bids</p>
							<p className="font-semibold text-sm">{auction.bids.length}</p>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-3 flex items-center gap-3">
						<div className="p-2 rounded-lg bg-primary/10">
							<Clock className="h-4 w-4 text-primary" />
						</div>
						<div>
							<p className="text-xs text-muted-foreground">Time Left</p>
							<CountdownTimer deadline={auction.deadline} compact />
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-3 flex items-center gap-3">
						<div className="p-2 rounded-lg bg-primary/10">
							<Briefcase className="h-4 w-4 text-primary" />
						</div>
						<div>
							<p className="text-xs text-muted-foreground">Lowest Bid</p>
							<p className="font-semibold text-sm">
								{auction.bids.length > 0
									? `$${Math.min(...auction.bids.map((b) => b.amount)).toLocaleString()}`
									: "No bids"}
							</p>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Countdown */}
			<Card>
				<CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
					<div className="flex items-center gap-3">
						<Avatar className="h-10 w-10">
							<AvatarFallback className="font-semibold text-sm bg-primary/10">
								{auction.buyer.avatar}
							</AvatarFallback>
						</Avatar>
						<div>
							<p className="font-medium text-sm">{auction.buyer.name}</p>
							<div className="flex items-center gap-2 text-xs text-muted-foreground">
								<span className="flex items-center gap-0.5">
									<Star className="h-3 w-3 fill-amber-400 text-amber-400" />
									{auction.buyer.rating}
								</span>
								<span>{auction.buyer.completedJobs} jobs</span>
							</div>
						</div>
					</div>
					<div className="text-center sm:text-right">
						<p className="text-xs text-muted-foreground mb-1 font-medium uppercase tracking-wider">
							Bidding {isClosed ? "ended" : "ends in"}
						</p>
						<CountdownTimer deadline={auction.deadline} />
					</div>
				</CardContent>
			</Card>

			{/* Main Content */}
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2">
					<Tabs defaultValue="bids">
						<TabsList className="w-full">
							<TabsTrigger value="bids" className="flex-1">
								Bids ({auction.bids.length})
							</TabsTrigger>
							<TabsTrigger value="discussion" className="flex-1">
								Discussion ({auction.comments.length})
							</TabsTrigger>
						</TabsList>
						<TabsContent value="bids" className="mt-4">
							<BidComparison bids={auction.bids} budget={auction.budget} />
						</TabsContent>
						<TabsContent value="discussion" className="mt-4">
							<CommentThread
								comments={auction.comments}
								onSubmit={async (text, parentId) => {
									await submitComment(auctionId, {
										authorId: "u1",
										text,
										parentId,
									});
									loadData();
								}}
							/>
						</TabsContent>
					</Tabs>
				</div>

				<div className="space-y-4">
					{!isClosed && (
						<BidForm
							auctionId={auctionId}
							budget={auction.budget}
							users={users}
							onBidSubmitted={() => loadData()}
						/>
					)}
					{isClosed && (
						<Card>
							<CardContent className="p-4 text-center text-muted-foreground">
								<Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
								<p className="font-medium text-sm">Bidding has ended</p>
								<p className="text-xs mt-1">
									This auction is no longer accepting bids
								</p>
							</CardContent>
						</Card>
					)}
				</div>
			</div>
		</div>
	);
}
